import React, { useState } from 'react'
import './price-sheet.css'

// --- mapovanie požadovaných kategórií na kľúče programov v dátach ---
const CATEGORY_TO_PROGRAM_KEYS = {
  'kids': ['kids'],                 // Bully proof deti do 13 r.
  'adults-beginers': ['combatives'],// Gracie Combatives začiatočníci
  'adults-pro': ['mc'],             // Master cycle pokročilí
  'we': ['we'],                     // Women Empowered ženy
}

const PROGRAM_LOGOS = {
  kids: {
    src: '/images/Gracie-Bullyproof-Lockup-Logo-COLOR.png',
    alt: 'Gracie Bullyproof',
  },
  combatives: {
    src: '/images/Gracie-Combatives-Lockup-Logo-COLOR-1200x273.png',
    alt: 'Gracie Combatives',
  },
  we: {
    src: '/images/we-logo.png',
    alt: 'Women Empowered',
  },
  mc: {
    src: '/images/Master-Cycle-Lockup-Logo-COLOR.png',
    alt: 'Master Cycle',
  },
}

function eur(n) {
  if (n == null) return '—'
  return new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n)
}

const YesNo = ({ v, mark = '' }) => (
  <td className="ps-cell ps-yn" style={{ background: v ? '#eaf7ee' : '#fbecec' }}>
    {v ? <>ÁNO{mark ? <span className="ps-mark">{mark}</span> : null}</> : '—'}
  </td>
)

// --- default dataset (ako na obrázku) ---
const defaultData = {
  title: "Členské poplatky - cenník",
  valid_from: "1. 9. 2026",
  programs: [
    { key: "kids", label: "Bully proof\ndeti do 13 r.", color: "#fde68a" },
    { key: "combatives", label: "Gracie Combatives\nzačiatočníci", color: "#c7f9cc" },
    { key: "we", label: "Women Empowered\nženy", color: "#fbcfe8" },
    { key: "mc", label: "Master cycle\npokročilí", color: "#bfdbfe" },
  ],
  sections: [
    {
      id: "adult-members",
      eyebrow: "Programy pre dospelých",
      title: "Gracie Combatives · Women Empowered · Master Cycle",
      description: "Začiatočnícke, ženské a pokročilé tréningové programy",
      columns: ["monthly", "family", "annual", "annualFamily", "entries"],
      rows: [
        { name: "Minimálny členský poplatok", monthly: 10, entries: "1×", pay: "mesačne", programs: { combatives: true, we: true, mc: true } },
        { name: "4× vstup za mesiac", monthly: 30, annual: 300, entries: "4×", programs: { combatives: true, we: true, mc: true } },
        { name: "8× vstup za mesiac", monthly: 50, family: 40, annual: 500, entries: "8×", programs: { combatives: true, we: true, mc: true }, marks: { we: "*", mc: "**" } },
        { name: "Dospelí", monthly: 60, family: 48, annual: 600, entries: "neobmedzene", programs: { combatives: true, we: true, mc: true }, marks: { we: "*", mc: "**" } },
      ],
      footnotes: [
        "* Women Empowered - program je určený výlučne pre ženy.",
        "** Master Cycle - pokročilí; určené pre úspešných absolventov Gracie Combatives."
      ]
    },
    {
      id: "kids-members",
      eyebrow: "Program pre deti",
      title: "Gracie Bullyproof",
      description: "Malí šampióni · Mladí zápasníci · Black Belt Club",
      columns: ["monthly", "family", "annual", "annualFamily", "entries"],
      rows: [
        { name: "4× vstup za mesiac", monthly: 30, annual: 240, annualMonths: 10, entries: "4×", programs: { kids: true } },
        { name: "Študenti", monthly: 50, family: 40, annual: 400, annualMonths: 10, entries: "neobmedzene", programs: { kids: true } },
      ],
    },
    {
      title: "Jednorazový vstup pre návštevníkov",
      layout: "visitor-pass",
      columns: ["monthly", "entries", "pay"],
      rows: [
        { name: "Návštevník, ktorý nie je naším študentom", monthly: 15, entries: "jedna hodina", pay: "platba pred tréningom", programs: { kids: true, combatives: true, we: true, mc: true } },
      ],
      notes: [
        "Jednorazový vstup je určený pre návštevníkov bez aktívneho členstva."
      ]
    },
    {
      title: "Individuálne (súkromné) hodiny",
      layout: "private-pass",
      columns: ["monthly", "entries", "pay"],
      rows: [
        { name: "Súkromná hodina", monthly: 40, entries: "jedna hodina", pay: "platba pred tréningom", programs: { kids: true, combatives: true, we: true, mc: true } },
        { name: "Balík 12 súkromných hodín", monthly: 400, entries: "12 hodín", pay: "platba vopred", programs: { kids: true, combatives: true, we: true, mc: true } },
      ]
    }
  ]
}

/** @param {{ data?: any, category?: string[] }} props */
export default function Pricelist({ data, category = [] }) {
  const [paymentPeriods, setPaymentPeriods] = useState({})
  // 1) default dataset ak nie sú dáta
  const effectiveData = data && Object.keys(data).length > 0 ? data : defaultData
  const programs = Array.isArray(effectiveData.programs) ? effectiveData.programs : []

  // 2) príprava filtra podľa category
  const normalizedCat = Array.isArray(category) ? category : [category].filter(Boolean)
  const isPrivate = normalizedCat.includes('private-class')
  const requestedProgramKeys = normalizedCat
    .flatMap(c => (c === 'private-class' ? [] : (CATEGORY_TO_PROGRAM_KEYS[c] || [])))
  const isFiltered = requestedProgramKeys.length > 0 || isPrivate

  // helper: je toto sekcia súkromných hodín?
  // const isPrivateSection = (sec) => typeof sec.title === 'string' && sec.title.startsWith('Individuálne')
  const isPrivateSection = (sec) => typeof sec.title === 'string' && sec.title.startsWith('Individuálne')
  const isVisitorSection = (sec) => typeof sec.title === 'string' && sec.title.startsWith('Jednorazový')
  const isVisitorPassSection = (sec) => sec.layout === 'visitor-pass'
  const isPrivatePassSection = (sec) => isPrivateSection(sec) && sec.layout === 'private-pass'
  const isCompactPassSection = (sec) => isVisitorPassSection(sec) || isPrivatePassSection(sec)
  const isMembershipSection = (sec) => sec.columns?.includes('annual')

  // 3) labely stĺpcov
  const colLabels = {
    name: "",
    monthly: "mesačná\nplatba",
    family: "mesačne – ďalší\nčlen rodiny\n(zľava 20 %)",
    entries: "Počet vstupov\nza mesiac",
    pay: "Platba",
    annual: "ročná\nplatba",
    annualFamily: "ročne – ďalší\nčlen rodiny",
    ...(effectiveData.column_labels || {})
  }

  const columnLabel = (section, key) => {
    if (key === 'monthly' && isVisitorSection(section)) {
      return 'Cena'
    }

    return colLabels[key]
  }

  // 4) pri filtrovaní zobrazíme len stĺpce po "pay" (okrem private-class sekcie)
  const BASE_COLS = ['monthly', 'family', 'annual', 'annualFamily', 'entries', 'pay']

  const renderHeaderCols = (section) => {
    const showAllColsThisSection = isPrivate && isPrivateSection(section)
    const cols = showAllColsThisSection
      ? section.columns
      : isFiltered
        ? section.columns.filter(c => BASE_COLS.includes(c))
        : section.columns

    return (
      <>
        <th className="ps-col-name"> </th>

        {cols.includes('monthly') && <th className="ps-payment-monthly"><span className="ps-multiline">{columnLabel(section, 'monthly')}</span></th>}
        {cols.includes('family') && <th className="ps-payment-monthly"><span className="ps-multiline">{columnLabel(section, 'family')}</span></th>}
        {cols.includes('annual') && <th className="ps-payment-annual"><span className="ps-multiline">{columnLabel(section, 'annual')}</span></th>}
        {cols.includes('annualFamily') && <th className="ps-payment-annual"><span className="ps-multiline">{columnLabel(section, 'annualFamily')}</span></th>}
        {cols.includes('entries') && <th><span className="ps-multiline">{columnLabel(section, 'entries')}</span></th>}
        {cols.includes('pay') && <th><span className="ps-multiline">{columnLabel(section, 'pay')}</span></th>}

        {/* programové stĺpce: len ak nie je filter, alebo ak ide o private sekciu pri private-class */}
      </>
    )
  }

  const renderRow = (r, sectionCols, section) => {
    const showAllColsThisSection = isPrivate && isPrivateSection(section)
    const cols = showAllColsThisSection
      ? sectionCols
      : isFiltered
        ? sectionCols.filter(c => BASE_COLS.includes(c))
        : sectionCols

    return (
      <>
        <td className="ps-cell ps-name">{r.name}</td>
        {cols.includes('monthly') && <td className="ps-cell">{eur(r.monthly)}</td>}
        {cols.includes('family') && <td className="ps-cell">{r.family != null ? eur(r.family) : '—'}</td>}
        {cols.includes('annual') && <td className="ps-cell ps-annual">{r.annual != null ? eur(r.annual) : '—'}</td>}
        {cols.includes('annualFamily') && <td className="ps-cell ps-annual">{r.annualFamily != null ? eur(r.annualFamily) : '—'}</td>}
        {cols.includes('entries') && <td className="ps-cell">{r.entries ?? '—'}</td>}
        {cols.includes('pay') && <td className="ps-cell">{r.pay ?? '—'}</td>}

      </>
    )
  }

  const visibleColsForSection = (section) => {
    const showAllColsThisSection = isPrivate && isPrivateSection(section)

    return showAllColsThisSection
      ? section.columns
      : isFiltered
        ? section.columns.filter(c => BASE_COLS.includes(c))
        : section.columns
  }

  const mobileDetailsForRow = (row, section) => {
    const cols = visibleColsForSection(section)

    return [
      cols.includes('monthly') && { label: isVisitorSection(section) ? 'Cena' : 'Mesačne', value: eur(row.monthly), highlight: true },
      cols.includes('family') && { label: 'Rodina', value: row.family != null ? eur(row.family) : '—' },
      cols.includes('annual') && { label: 'Ročne', value: row.annual != null ? eur(row.annual) : '—', annual: true },
      cols.includes('annualFamily') && { label: 'Ročne – rodina', value: row.annualFamily != null ? eur(row.annualFamily) : '—', annual: true },
      cols.includes('entries') && { label: 'Vstupy', value: row.entries ?? '—' },
      cols.includes('pay') && { label: 'Platba', value: row.pay ?? '—' },
    ].filter(Boolean)
  }

  const mobileProgramBadgesForRow = (row, section) => {
    return []
    /* Program je zrejmý z názvu samostatnej sekcie cenníka.
    const showAllColsThisSection = isPrivate && isPrivateSection(section)

    if (isFiltered && !showAllColsThisSection) {
      return []
    }

    return programs
      .filter((program) => row.programs?.[program.key])
      .map((program) => ({
        ...program,
        mark: row.marks?.[program.key] ?? '',
      }))
    */
  }

  // const sectionHasRows = (sec) => {
  //   if (!isFiltered) return (sec.rows || []).length > 0
  //   if (isPrivate && isPrivateSection(sec)) return true // vždy zobraz celú private sekciu
  //   const rows = sec.rows || []
  //   return rows.some(r => requestedProgramKeys.some(k => r.programs?.[k]))
  // }
  const sectionHasRows = (sec) => {
    const rows = sec.rows || []
    if (!isFiltered) return rows.length > 0

    if (isPrivateSection(sec)) {
      // private sekcia sa pri filtroch zobrazí len ak je zapnutý prepínač
      return isPrivate
    }

    if (isVisitorPassSection(sec)) {
      return !isFiltered
    }

    // bežné sekcie: aspoň jeden riadok musí mať program z requestedProgramKeys
    return rows.some(r => requestedProgramKeys.some(k => r.programs?.[k]))
  }

  // const filteredRows = (sec) => {
  //   if (!isFiltered) return sec.rows || []
  //   if (isPrivate && isPrivateSection(sec)) return sec.rows || [] // žiadna filtrácia v private sekcii
  //   return (sec.rows || []).filter(r => requestedProgramKeys.some(k => r.programs?.[k]))
  // }

  const filteredRows = (sec) => {
    if (!isFiltered) return sec.rows || []

    if (isPrivateSection(sec)) {
      // ak je private sekcia a nie je zapnutý prepínač, úplne ju skryj
      return isPrivate ? (sec.rows || []) : []
    }

    if (isVisitorPassSection(sec)) {
      return []
    }

    // bežné sekcie: necháme len riadky, ktoré spĺňajú aspoň jeden z požadovaných programov
    return (sec.rows || []).filter(r => requestedProgramKeys.some(k => r.programs?.[k]))
  }

  const paymentPeriodForSection = (section) => paymentPeriods[section.id] || 'monthly'

  const setPaymentPeriodForSection = (section, period) => {
    setPaymentPeriods((current) => ({ ...current, [section.id]: period }))
  }

  const priceForRow = (row, paymentPeriod) => {
    if (paymentPeriod === 'annual') {
      return row.annual
    }

    return row.monthly
  }

  const annualSavingForRow = (row) => {
    const monthlyPrice = row.monthly
    const annualPrice = row.annual
    const annualMonths = row.annualMonths ?? 12

    if (monthlyPrice == null || annualPrice == null) return 0
    return Math.max(0, (monthlyPrice * annualMonths) - annualPrice)
  }

  const visibleSections = (effectiveData.sections || []).filter(sectionHasRows)
  const filteredProgramHeadings = {
    kids: {
      eyebrow: 'Program pre deti',
      title: 'Gracie Bullyproof',
      description: 'Malí šampióni · Mladí zápasníci · Black Belt Club',
    },
    combatives: {
      eyebrow: 'Program pre dospelých',
      title: 'Gracie Combatives',
      description: 'Začiatočnícky program a Reflex Development',
    },
    we: {
      eyebrow: 'Program pre ženy',
      title: 'Women Empowered',
      description: 'Program sebaobrany určený výlučne pre ženy',
    },
    mc: {
      eyebrow: 'Program pre pokročilých',
      title: 'Master Cycle',
      description: 'Pokročilý program pre absolventov Gracie Combatives',
    },
  }

  const headingForSection = (section) => {
    if (requestedProgramKeys.length === 1 && filteredProgramHeadings[requestedProgramKeys[0]]) {
      return filteredProgramHeadings[requestedProgramKeys[0]]
    }

    return section
  }

  const logosForSection = (section) => {
    const logoKeys = requestedProgramKeys.length === 1
      ? requestedProgramKeys
      : section.id === 'kids-members'
        ? ['kids']
        : ['combatives', 'we', 'mc']

    return logoKeys.map((key) => PROGRAM_LOGOS[key]).filter(Boolean)
  }

  const renderPeriodSwitch = (section, extraClass = '') => (
    <div
      className={`ps-period-switch${extraClass ? ` ${extraClass}` : ''}`}
      role="group"
      aria-label="Frekvencia platby"
    >
      <button
        type="button"
        className={paymentPeriodForSection(section) === 'monthly' ? 'is-active' : ''}
        onClick={() => setPaymentPeriodForSection(section, 'monthly')}
      >
        Mesačná platba
      </button>
      <button
        type="button"
        className={paymentPeriodForSection(section) === 'annual' ? 'is-active' : ''}
        onClick={() => setPaymentPeriodForSection(section, 'annual')}
      >
        Ročná platba
      </button>
    </div>
  )

  return (
    <section className="hg_section hg_section--relative">
      <div className="container">
        <div className="row">
          <div className="col-sm-12 col-md-12">
            <div className="row">

              <div className="price-sheet">
                <div className="ps-titlebar">
                  <h2>{effectiveData.title || 'členské poplatky - cenník'}</h2>
                  {effectiveData.valid_from && <div className="ps-valid">platný od {effectiveData.valid_from}</div>}
                </div>

                {visibleSections.map((sec, idx) => (
                  <div key={idx}>
                    {isMembershipSection(sec) ? (
                      <header className={`ps-program-heading ps-program-heading--${sec.id}`}>
                        <div className="ps-program-heading__copy">
                          <span>{headingForSection(sec).eyebrow}</span>
                          <h3>{headingForSection(sec).title}</h3>
                          {headingForSection(sec).description && (
                            <p>{headingForSection(sec).description}</p>
                          )}
                        </div>
                        <div className="ps-program-heading__logos">
                          {logosForSection(sec).map((logo) => (
                            <div className="ps-program-heading__logo" key={logo.alt}>
                              <img src={logo.src} alt={logo.alt} />
                            </div>
                          ))}
                        </div>
                      </header>
                    ) : isCompactPassSection(sec) ? (
                      <header
                        className={
                          `ps-service-heading ps-service-heading--${
                            isPrivatePassSection(sec) ? 'private' : 'visitor'
                          }`
                        }
                      >
                        <span>
                          {isPrivatePassSection(sec) ? 'Individuálny tréning' : 'Pre návštevníkov'}
                        </span>
                        <h3>{sec.title}</h3>
                      </header>
                    ) : (
                      <h3 className="ps-section">{sec.title}</h3>
                    )}
                    {isMembershipSection(sec) && (
                      <div className="ps-section-period-mobile" aria-label="Nastavenie cien">
                        {renderPeriodSwitch(sec)}
                      </div>
                    )}
                    {isMembershipSection(sec) ? (
                      <div className="ps-compact-pricing">
                        <div className="ps-compact-pricing__header">
                          <div className="ps-compact-pricing__header-main">
                            <span>Členstvo</span>
                            {renderPeriodSwitch(sec, 'ps-period-switch--header')}
                          </div>
                          <span>{paymentPeriodForSection(sec) === 'annual' ? 'Ročná platba' : 'Mesačná platba'}</span>
                          <span>Ďalší člen rodiny</span>
                          <span>Vstupy za mesiac</span>
                        </div>
                        {filteredRows(sec).map((row) => {
                          const paymentPeriod = paymentPeriodForSection(sec)
                          const price = priceForRow(row, paymentPeriod)
                          const familyPrice = paymentPeriod === 'annual' ? row.annualFamily : row.family
                          const saving = annualSavingForRow(row)

                          return (
                            <article className="ps-compact-pricing__row" key={row.name}>
                              <div className="ps-compact-pricing__name">
                                <strong>{row.name}</strong>
                                <small>{row.entries === 'neobmedzene' ? 'bez obmedzenia' : `${row.entries} za mesiac`}</small>
                              </div>
                              <div className="ps-compact-pricing__price">
                                <span>{paymentPeriod === 'annual' ? 'Ročne' : 'Mesačne'}</span>
                                <strong>{price != null ? eur(price) : '—'}</strong>
                                {paymentPeriod === 'annual' && saving > 0 && (
                                  <small>úspora {eur(saving)}</small>
                                )}
                              </div>
                              <div className="ps-compact-pricing__family">
                                <span>Rodinná cena</span>
                                <strong>{familyPrice != null ? eur(familyPrice) : '—'}</strong>
                              </div>
                              <div className="ps-compact-pricing__entries">
                                <span>Vstupy za mesiac</span>
                                <strong>{row.entries}</strong>
                              </div>
                            </article>
                          )
                        })}
                        {sec.id === 'adult-members' && (
                          <div className="ps-compact-pricing__extra-session">
                            <div>
                              <span>Hodina navyše</span>
                              <strong>Každá ďalšia tréningová hodina nad rámec balíka</strong>
                            </div>
                            <strong>10 €</strong>
                          </div>
                        )}
                      </div>
                    ) : isCompactPassSection(sec) ? (
                      <div className="ps-visitor-pass">
                        {(sec.rows || []).map((r, i) => (
                          <article className="ps-visitor-pass__card" key={i}>
                            <div>
                              <span>
                                {isPrivatePassSection(sec)
                                  ? 'Individuálny tréning s trénerom'
                                  : 'Pre návštevníkov bez aktívneho členstva'}
                              </span>
                              <strong>{r.name}</strong>
                            </div>
                            <div className="ps-visitor-pass__meta">
                              {[r.entries, r.pay].filter(Boolean).join(' / ')}
                            </div>
                            <div className="ps-visitor-pass__price">{eur(r.monthly)}</div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <>
                        <div className="ps-table-wrap">
                          <table className="ps-table">
                            <thead>
                              <tr>{renderHeaderCols(sec)}</tr>
                            </thead>
                            <tbody>
                              {filteredRows(sec).map((r, i) => (
                                <tr key={i}>{renderRow(r, sec.columns, sec)}</tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="ps-mobile-cards">
                          {filteredRows(sec).map((r, i) => (
                            <article className="ps-mobile-card" key={i}>
                              <h4>{r.name}</h4>

                              <dl className="ps-mobile-card__details">
                                {mobileDetailsForRow(r, sec).map((detail) => (
                                  <div
                                    className={detail.highlight ? 'ps-mobile-card__price' : ''}
                                    key={detail.label}
                                  >
                                    <dt>{detail.label}</dt>
                                    <dd>{detail.value}</dd>
                                  </div>
                                ))}
                              </dl>

                              {mobileProgramBadgesForRow(r, sec).length > 0 && (
                                <div className="ps-mobile-card__programs">
                                  {mobileProgramBadgesForRow(r, sec).map((program) => (
                                    <span style={{ background: program.color }} key={program.key}>
                                      {program.label.split('\n')[0]}
                                      {program.mark && <sup>{program.mark}</sup>}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </article>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Poznámky / poznámky k sekcii ponecháme, dávajú zmysel aj pri filtri */}
                    {Array.isArray(sec.notes) && sec.notes.length > 0 && (
                      <ul className="ps-notes">{sec.notes.map((t, i) => <li key={i}>{t}</li>)}</ul>
                    )}
                    {Array.isArray(sec.footnotes) && sec.footnotes.length > 0 && (
                      <div className="ps-footnotes">{sec.footnotes.map((t, i) => <div key={i}>{t}</div>)}</div>
                    )}
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
